import React, { useState, useEffect } from 'react';
import NOCMetrics from './NOCMetrics';
import NOCEventsFeed from './NOCEventsFeed';
import NOCAlertsList from './NOCAlertsList';
import NOCUserManagement from './NOCUserManagement';
import NOCAttackSimulator from './NOCAttackSimulator';
import { NOC_API_ENDPOINTS, nocApiRequest } from '../config/api';

export type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type AlertType = 'RATE_ABUSE' | 'GUARDRAIL_EVASION' | 'ABUSIVE_CONTENT' | 'ANOMALY';
export type AlertStatus = 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED';
export type UserStatus = 'ACTIVE' | 'WARNING' | 'THROTTLED' | 'BLOCKED';

export interface AuditEvent {
  request_id: string;
  timestamp: string;
  user_id: string;
  session_id: string;
  interaction_type: string;
  topic: string;
  blocked: boolean;
  block_reason?: string;
  classification?: 'SAFE' | 'SUSPICIOUS' | 'ABUSIVE';
  risk_score?: number;
  ip_address?: string;
}

export interface Alert {
  alert_id: string;
  timestamp: string;
  severity: Severity;
  alert_type: AlertType;
  user_id: string;
  session_id: string;
  description: string;
  recommendation: string;
  status: AlertStatus;
  evidence_count: number;
  resolved_by?: string;
  action_taken?: string;
}

export interface UserStatusInfo {
  user_id: string;
  status: UserStatus;
  blocked_at?: string;
  blocked_by?: string;
  blocked_reason?: string;
  alert_count: number;
  last_alert_at?: string;
  request_count_24h: number;
}

const NOCDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'alerts' | 'users' | 'simulator'>('dashboard');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [metrics, setMetrics] = useState({
    requestsPerMin: 0,
    blocksPerMin: 0,
    activeAlerts: 0,
    blockedUsers: 0
  });

  const handleUserClick = (userId: string) => {
    setSelectedUserId(userId);
    setActiveTab('users');
  };

  // Fetch real metrics from API
  const fetchMetrics = async () => {
    try {
      const data = await nocApiRequest(NOC_API_ENDPOINTS.metrics);
      setMetrics({
        requestsPerMin: data.requests_per_min || 0,
        blocksPerMin: data.blocks_per_min || 0,
        activeAlerts: data.active_alerts || 0,
        blockedUsers: data.blocked_users || 0
      });
    } catch (error) {
      console.error('Failed to fetch NOC metrics:', error);
      // Keep existing metrics on error
    }
  };

  // Fetch metrics on mount and every 5 seconds
  useEffect(() => {
    fetchMetrics(); // Initial fetch
    const interval = setInterval(fetchMetrics, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h1 className="text-xl font-bold text-white">AI Events - Network Operations Center</h1>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-300">Live Monitoring</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-3 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'dashboard'
                  ? 'border-red-500 text-white'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('alerts')}
              className={`px-3 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'alerts'
                  ? 'border-red-500 text-white'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Alerts
              {metrics.activeAlerts > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-red-600 text-white rounded-full">
                  {metrics.activeAlerts}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-3 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'users'
                  ? 'border-red-500 text-white'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              User Management
            </button>
            <button
              onClick={() => setActiveTab('simulator')}
              className={`px-3 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'simulator'
                  ? 'border-red-500 text-white'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Attack Simulator
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <NOCMetrics metrics={metrics} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <NOCEventsFeed onUserClick={handleUserClick} />
              <NOCAlertsList showAll={false} onUserClick={handleUserClick} />
            </div>
          </div>
        )}

        {activeTab === 'alerts' && <NOCAlertsList showAll={true} onUserClick={handleUserClick} />}

        {activeTab === 'users' && <NOCUserManagement selectedUserId={selectedUserId} />}

        {activeTab === 'simulator' && <NOCAttackSimulator />}
      </main>
    </div>
  );
};

export default NOCDashboard;
